import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { NISStatusList } from "../../../../../../api/services/nisParticipants/models";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../../../components";
import { StatusBadge } from "../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../helpers/types/common";
import {
  convertToUserFormat,
  getFullName,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { ViewNISParticipantStatusResultKey } from "../../../../modelsAndSchemas/Models";
import { createNISParticipantNISStatusSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { NISStatusResultWrapper } from "./NISStatusResult.style";

interface NISStatusResultProps {
  rows: NISStatusList[];
  isEditNISParticipant?: boolean;
}

const pageId = PAGE_IDS.NIS_STATUS_RESULT;

export const NISStatusResult: FC<NISStatusResultProps> = ({
  rows,
  isEditNISParticipant,
}) => {
  const { useFetchLabel, commonLabels, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [allUpdatedRows, setAllUpdatedRows] = useState<NISStatusList[]>([]);
  const [uniqueStatusFilter, setUniqueStatusFilter] = useState<LabelValue[]>(
    [],
  );

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    nisStatusTitle,
    createdModifiedByTableColumn,
    createdModifiedDateTableColumn,
    notesTableColumn,
    statusTableColumn,
    nisParticipantStatusList,
  } = createNISParticipantNISStatusSiteCoreKeys;

  const getLabel = useFetchLabel(nisStatusTitle);

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  const getUniqueStatus = (
    data: NISStatusList[],
    key: string,
  ): LabelValue[] => {
    const uniqueValues = Array.from(
      new Set(data.map((item) => item[key]).filter(Boolean)), // filter out null/undefined
    );

    return uniqueValues.map((value) => ({
      value: value as string,
      label: value as string,
    }));
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = structuredClone(rows);

      localData.forEach((data) => {
        data.statusKey = data.statusId;
        data.statusId = fetchValueByKeyOfDropdown(
          nisStatusTitle,
          nisParticipantStatusList,
          data.statusId,
        );
      });

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
      setUniqueStatusFilter(
        getUniqueStatus(localData, ViewNISParticipantStatusResultKey.statusId),
      );
    }
  }, [rows, siteCoreLoading]);

  const { control, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [ViewNISParticipantStatusResultKey.createdDate]: "",
      [ViewNISParticipantStatusResultKey.statusId]: "",
      [ViewNISParticipantStatusResultKey.createdBy]: "",
      [ViewNISParticipantStatusResultKey.notes]: "",
    },
  });

  const formValues = useWatch({
    control,
  });

  useEffect(() => {
    const { createdDate, statusId, createdBy } = getValues();
    if (!isEmpty(allUpdatedRows)) {
      const filteredData = allUpdatedRows.filter((item) => {
        const createdDateMatch =
          !createdDate ||
          dayjs(item?.createdDate).isSame(
            dayjs(createdDate, userCountryDateFormat),
            "day",
          );

        const statusMatch =
          statusId === "" ||
          item.statusId.toLowerCase() === statusId.toLowerCase();

        const createdByName = getFullName(
          item?.createdBy?.firstName,
          item?.createdBy?.lastName,
        );
        const createdNameMatch =
          !createdBy ||
          !createdByName ||
          createdByName.toLowerCase().includes(createdBy.toLowerCase());

        return createdDateMatch && statusMatch && createdNameMatch;
      });
      setFilteredResults([...filteredData]);
      resetPagination();
    }
  }, [formValues]);

  const columns = [
    {
      id: ViewNISParticipantStatusResultKey.createdDate,
      label: getLabel(createdModifiedDateTableColumn),
      classNames: "cell-created-date",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.createdDate)
              ? convertToUserFormat(row?.createdDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: ViewNISParticipantStatusResultKey.statusId,
      label: getLabel(statusTableColumn),
      isSortVisible: true,
      classNames: "cell-status",
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.statusId} />
      ),
    },
    {
      id: ViewNISParticipantStatusResultKey.createdBy,
      label: getLabel(createdModifiedByTableColumn),
      isSortVisible: true,
      classNames: "cell-createdBy",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row?.createdBy)
              ? getFullName(row?.createdBy?.firstName, row?.createdBy?.lastName)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewNISParticipantStatusResultKey.notes,
      label: getLabel(notesTableColumn),
      isSortVisible: true,
      classNames: "cell-notes",
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row?.notes} />
        </MuiBox>
      ),
    },
  ];

  const tableHeaderForm = [
    <FormController
      key={ViewNISParticipantStatusResultKey.createdDate}
      control={control}
      name={ViewNISParticipantStatusResultKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          name={ViewNISParticipantStatusResultKey.createdDate}
          clearable
          value={
            getValues(ViewNISParticipantStatusResultKey.createdDate) !== ""
              ? dayjs(
                  getValues(ViewNISParticipantStatusResultKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              ViewNISParticipantStatusResultKey.createdDate,
              newValue?.format(userCountryDateFormat) || "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      key={ViewNISParticipantStatusResultKey.statusId}
      control={control}
      name={ViewNISParticipantStatusResultKey.statusId}
    >
      <MuiSelect
        label=""
        placeholder={commonLabels.selectPlaceHolder}
        options={uniqueStatusFilter}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${ViewNISParticipantStatusResultKey.statusId}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={ViewNISParticipantStatusResultKey.createdBy}
      control={control}
      name={ViewNISParticipantStatusResultKey.createdBy}
    >
      <MuiTextField
        data-testid="filter-created-by"
        id={`${pageId}-${ViewNISParticipantStatusResultKey.createdBy}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
  ];

  return (
    <NISStatusResultWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm as ReactElement[]}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={rows?.length === 0}
        priorityResults={[]}
        noResultMessage={commonLabels.searchCriteria}
        isLastColumnSticky={false}
      />
    </NISStatusResultWrapper>
  );
};
